import React, { forwardRef, useEffect, useState } from 'react';
import { forceVisible } from 'react-lazyload';
import cn from 'clsx';

import Button from '../../library/button/button';
import ProductSpecifications from '../product-specifications/product-specifications';

import { QueryFiltersState } from '../../pages/product-detail-page/hooks/use-pdp-product/use-pdp-product';

import { Fitment } from '../../shared/types/fitment';
import { Maybe, ProductData, ToolTips } from '../../shared/types/graphql-types';

import styles from './product-details.scss';

interface ProductDetailsProps {
  className?: string;
  hideSizeTable?: boolean;
  isCanonicalSearch: boolean;
  onSizeSelected?: (selectedFilters) => void;
  product: ProductData;
  productType: string;
  queryFilters: QueryFiltersState[] | null;
  selectedFitment: Fitment;
  shouldForceRenderLazyChildren?: boolean;
  storeCode?: Maybe<string>;
  tooltips: ToolTips;
}

const ProductDetails = forwardRef<HTMLDivElement, ProductDetailsProps>(
  function ProductDetails(props, ref) {
    const {
      className = '',
      hideSizeTable,
      isCanonicalSearch,
      onSizeSelected,
      product,
      productType,
      queryFilters,
      selectedFitment,
      shouldForceRenderLazyChildren,
      storeCode,
      tooltips
    } = props;

    const [descriptionExpanded, setDescriptionExpanded] = useState(false);

    useEffect(() => {
      if (shouldForceRenderLazyChildren) {
        forceVisible();
      }
    }, [shouldForceRenderLazyChildren]);

    return (
      <div
        className={cn(
          styles['product-details'],
          `product-details ${className}`
        )}
        ref={ref}
      >
        {(product?.longDescription || product?.description) && (
          <div
            className={cn(
              styles['product-description'],
              'product-details__product-description'
            )}
          >
            <div
              className={cn(
                descriptionExpanded
                  ? styles['description-expanded']
                  : styles['description-collapsed'],
                'cms-content'
              )}
              dangerouslySetInnerHTML={{
                __html: product?.longDescription ?? product.description ?? ''
              }}
            />

            {product?.longDescription && (
              <Button
                className={styles['product-description-expansion-button']}
                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
              >
                {descriptionExpanded
                  ? 'read less'
                  : 'read full product description'}
              </Button>
            )}
          </div>
        )}
        {product && (
          <ProductSpecifications
            className={styles['product-specifications']}
            hideSizeTable={hideSizeTable}
            isCanonicalSearch={isCanonicalSearch}
            onSizeSelected={onSizeSelected}
            product={product}
            productType={productType}
            queryFilters={queryFilters}
            selectedFitment={selectedFitment}
            storeCode={storeCode}
            tooltips={tooltips}
          />
        )}
      </div>
    );
  }
);

export default ProductDetails;
